// Generated by tutorials/record.cjs from tutorials/articles/h15-running-a-report.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h15 --text-only` (wording) or
// `node tutorials/record.cjs h15` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'running-a-report',
  catalogId: 'h15',
  title: 'Running a report',
  summary: 'Find the report you need, choose its options, and print it or save it. Dietech makes the report while you keep working, tells you when it is ready, and keeps it in the Print Queue.',
  audience: 'Everyone who prints or saves reports',
  area: 'reports',
  learn: [
    'Find a report by name or by group',
    'Set its options and generate it',
    'Know when it is ready, and open it',
    'Print it, or save it as PDF, Excel or Word',
    'Find it again later in the Print Queue',
    'Have Dietech make it for you every day, week or month',
    'Change, pause or stop a schedule',
  ],
  keywords: [
    'report',
    'reports',
    'run a report',
    'generate',
    'print',
    'print report',
    'save report',
    'pdf',
    'excel',
    'word',
    'download',
    'export',
    'diet order report',
    'census report',
    'report options',
    'schedule',
    'scheduled report',
    'scheduled reports',
    'every day',
    'automatic',
    'pause schedule',
    'downloads folder',
    'where is my file',
  ],
  steps: [
    {
      id: 'open-reports',
      part: 'Find the report',
      heading: 'Open Reports',
      text: 'Select Reports in the menu. Every report you can run is a card, in groups such as Meal Service, Clinical, and Census & Rosters.',
      media: {
        image: {
          src: '/help/running-a-report/open-reports.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/open-reports.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.7,
        },
        alt: 'The Reports page: a search box, a row of group buttons, and cards for each report.',
      },
    },
    {
      id: 'search',
      part: 'Find the report',
      heading: 'Search for it',
      text: 'Type part of the report\'s name, or what it is for, in Search reports. You can also select a group, such as Census & Rosters, to see just those reports.',
      media: {
        image: {
          src: '/help/running-a-report/search.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/search.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.3,
        },
        alt: 'Search reports with "diet order" typed, and the matching report cards below it.',
      },
    },
    {
      id: 'open-report',
      part: 'Find the report',
      heading: 'Open the report',
      text: 'Select the report\'s card. Its options open, with the most common choices already filled in.',
      media: {
        image: {
          src: '/help/running-a-report/open-report.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/open-report.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.9,
        },
        alt: 'The Diet Order report\'s options: Report Date, and Order with By Diet Order selected.',
      },
    },
    {
      id: 'options',
      part: 'Set its options and generate it',
      heading: 'Choose the options',
      text: 'Each report asks only for what it needs. For Diet Order: the date, and how to sort it. Here, By Unit lists the residents unit by unit.',
      media: {
        image: {
          src: '/help/running-a-report/options.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/options.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.4,
        },
        alt: 'The Diet Order options with By Unit selected under Order.',
      },
    },
    {
      id: 'generate',
      part: 'Set its options and generate it',
      heading: 'Generate it',
      text: 'Select Generate at the bottom. Dietech makes the report in the background, so you can keep working. A message says it was sent to the Print Queue.',
      media: {
        image: {
          src: '/help/running-a-report/generate.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/generate.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.8,
        },
        alt: 'A message at the bottom of the screen: Diet Order queued. It appears in the Print Queue. With an Open Print Queue button.',
      },
    },
    {
      id: 'ready',
      part: 'Set its options and generate it',
      heading: 'When it is ready',
      text: 'When the report is ready, a message says so. Select View to open it.',
      media: {
        image: {
          src: '/help/running-a-report/ready.webp',
          width: 1280,
          height: 748,
        },
        alt: 'A message: "Diet Order" is ready, with a View button.',
      },
    },
    {
      id: 'view',
      part: 'Set its options and generate it',
      heading: 'Read it on screen',
      text: 'The report opens on screen. Use the arrows at the top to move between pages, and the magnifying glasses to zoom in and out.',
      media: {
        image: {
          src: '/help/running-a-report/view.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/view.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.4,
        },
        alt: 'The report viewer showing page 1 of the Diet Order report for Maple Grove Care Center.',
      },
    },
    {
      id: 'print',
      part: 'Set its options and generate it',
      heading: 'Print it',
      text: 'Select the printer button at the top of the report to print it. Pick your printer in the window that opens.',
      media: {
        image: {
          src: '/help/running-a-report/print.webp',
          width: 1280,
          height: 760,
        },
        alt: 'The Print button at the top of the report viewer, marked.',
      },
    },
    {
      id: 'save',
      part: 'Set its options and generate it',
      heading: 'Save a copy',
      text: 'To save a copy instead, select the download button beside it and choose PDF, Excel or Word.\n\nYour browser saves the file, named after the report: usually in your Downloads folder, or wherever your browser asks you to put it.',
      media: {
        image: {
          src: '/help/running-a-report/save.webp',
          width: 1280,
          height: 760,
        },
        clip: {
          src: '/help/running-a-report/save.mp4',
          width: 1280,
          height: 760,
          durationSec: 2.6,
        },
        alt: 'The download menu open in the report viewer, offering PDF, Excel and Word.',
      },
    },
    {
      id: 'in-queue',
      part: 'Find a report again',
      heading: 'It is kept in the Print Queue',
      text: 'Close the report. Every report you make is kept in the Print Queue, under Reports in the menu, so you can open or print it again later. The one you just made is at the top.',
      media: {
        image: {
          src: '/help/running-a-report/in-queue.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/in-queue.mp4',
          width: 1280,
          height: 748,
          durationSec: 4,
        },
        alt: 'The Print Queue with the new Diet Order report at the top, marked.',
      },
    },
    {
      id: 'schedule',
      part: 'Run it on a schedule',
      heading: 'Schedule instead of Generate',
      text: 'Need the same report at the same time every day, week or month? Open it and set its options as you would to print it, then select Schedule instead of Generate. The Schedule Report window opens.\n\nEach run uses the options you just set, so set them first.',
      media: {
        image: {
          src: '/help/running-a-report/schedule.webp',
          width: 944,
          height: 1146,
        },
        clip: {
          src: '/help/running-a-report/schedule.mp4',
          width: 944,
          height: 1146,
          durationSec: 2,
        },
        alt: 'The Schedule Report window for Diet Order, with the schedule name Diet Order - Daily and Frequency set to Daily.',
      },
    },
    {
      id: 'schedule-when',
      part: 'Run it on a schedule',
      heading: 'Choose how often and when',
      text: 'Under Frequency, pick Daily, Weekly or Monthly. For Weekly, check the days it runs. For Monthly, type the day of the month.\n\nTime is when it runs, on your facility\'s clock. Give the schedule a name you will know it by.',
      media: {
        image: {
          src: '/help/running-a-report/schedule-when.webp',
          width: 944,
          height: 1146,
        },
        clip: {
          src: '/help/running-a-report/schedule-when.mp4',
          width: 944,
          height: 1146,
          durationSec: 3.1,
        },
        alt: 'Frequency set to Weekly, with Mon to Fri checked under Days of Week, and the time 6:00 AM.',
      },
    },
    {
      id: 'schedule-dates',
      part: 'Run it on a schedule',
      heading: 'Choose the date each run covers',
      text: 'Under Report Dates on Each Run, pick the date the report is for, such as The run day or The day after the run day. The line below it shows the date a run today would use.\n\nUnder Run Between you can also give a first and a last date, for a schedule that should end.',
      media: {
        image: {
          src: '/help/running-a-report/schedule-dates.webp',
          width: 944,
          height: 1146,
        },
        alt: 'Report Dates on Each Run with Report Date set to The run day, and the date a run today would use below it.',
      },
    },
    {
      id: 'schedule-save',
      part: 'Run it on a schedule',
      heading: 'Save the schedule',
      text: 'Under Email Notices you can ask for an email when the report is ready, or if it does not finish. The email only names the report and links to the Print Queue. It never has the report or any resident information in it.\n\nSelect Save.',
      media: {
        image: {
          src: '/help/running-a-report/schedule-save.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/schedule-save.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.5,
        },
        alt: 'A message at the bottom of the screen: Schedule created. Its runs appear in the Print Queue.',
      },
    },
    {
      id: 'scheduled-list',
      part: 'Run it on a schedule',
      heading: 'Where scheduled reports go',
      text: 'Each time a schedule runs, Dietech makes the report and puts it in your Print Queue, ready to open and print. It does not print by itself.\n\nTo see your schedules, select Scheduled Reports under Reports in the menu. Each card shows how often it runs, when it runs next, and how its last run went.',
      media: {
        image: {
          src: '/help/running-a-report/scheduled-list.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/running-a-report/scheduled-list.mp4',
          width: 1280,
          height: 748,
          durationSec: 4,
        },
        alt: 'Scheduled Reports with two cards: Census - Daily, whose last run was a Success, and the new Diet Order schedule, which runs on weekdays at 6:00 AM. Both are Active.',
      },
    },
    {
      id: 'schedule-change',
      part: 'Run it on a schedule',
      heading: 'Change, pause or stop a schedule',
      text: 'Use the buttons at the bottom of a card. The pencil (Edit) changes when it runs, and Change report options in that window changes the report\'s options. Pause stops it until you select Activate. The clock shows each run so far, and the trash can deletes the schedule.\n\nRun Now makes the report straight away, without waiting for its time.',
      media: {
        image: {
          src: '/help/running-a-report/schedule-change.webp',
          width: 784,
          height: 392,
        },
        alt: 'The Diet Order schedule card, with Run Now and the Pause, Edit, View History and Delete buttons at the bottom marked.',
      },
    },
  ],
  tips: [
    'Run a report every day? Star it, and it is listed under Favorites in the menu. See Favorites and the print queue.',
    'You do not have to wait on the report page. Go on working: the message that it is ready appears on any page under Reports.',
  ],
  notes: [
    'The options a report offers depend on the report. Some only need a date; others let you pick units, meals or residents.',
    'Opening a report marks it Viewed in the Print Queue, and printing it marks it Printed, so you can tell which reports have been seen.',
    'A scheduled report always uses your facility\'s active menu cycle. If you are working in a Temp Menu, Dietech asks you to clear it before you schedule.',
    'If Scheduled Reports says scheduled reports are not running yet, the schedules you save start once scheduling is switched on for your facility. Ask Dietech support if you are not sure.',
  ],
  related: ['favorites-and-print-queue', 'getting-around', 'printers-and-printing'],
  recorded: '2026-10-28',
};

export default article;
